<template>
	<div
		ref="tableWrapper"
		class="dashboard-simple-table"
		:class="headerBorderColor ? 'dashboard-simple-table--color-' + headerBorderColor : ''"
	>
		<!-- Заглушка вместо таблицы: у пустой таблицы остаётся одна шапка, и она не сообщает,
		     что данных нет. Содержимое по умолчанию — здесь, а не у каждого виджета: заглушка
		     одна на все таблицы, а слот оставлен для тех, кому нужен свой текст.
		     Проп необязателен, поэтому таблицы, которые его не передают, ведут себя как раньше -->
		<slot
			v-if="isEmpty"
			name="empty"
		>
			<!-- Иконка и текст — через слоты, а не пропами: собственное оформление
			     `EmptyStateBlock` рассчитано на пустую панель во весь экран и в плитке
			     дашборда занимает её целиком. Размеры берутся из варианта: в невысокой
			     карточке уместен мелкий текст, в виджете на половину экрана он теряется -->
			<EmptyStateBlock class="w-100 h-100">
				<template #icon>
					<ao-icon
						iconName="tableRounded"
						:size="emptySizes.iconSize"
						:class="['text-textMuted', emptySizes.iconIndentClass]"
					/>
				</template>
				<template #info>
					<div class="d-flex flex-column align-center">
						<div :class="[emptySizes.titleClass, 'text-textSecondary700', 'text-center']">
							{{ $t('No data') }}
						</div>
						<div :class="[emptySizes.subtitleClass, 'text-textMuted', 'text-center']">
							{{ $t('There is no data for the selected period') }}
						</div>
					</div>
				</template>
			</EmptyStateBlock>
		</slot>
		<v-table
			v-else
			:fixed-header="true"
			:fixed-footer="true"
			:density="density"
			:height="autoHeight ? calculatedHeight || 'auto' : 'auto'"
		>
			<thead v-if="!hideHeading">
				<tr v-if="Boolean(titleHeader.length)">
					<th
						v-for="(col, ind) in titleHeader"
						:key="col.key"
						style="box-shadow: none"
						:class="`${ind !== 0 ? 'text-right' : 'text-left'} width:auto`"
					>
						{{ col?.title ?? col }}
					</th>
				</tr>

				<tr>
					<th
						v-for="(col, ind) in cols"
						:key="col.key"
						:class="[
							`${col.getHeadingCustomClass ? col.getHeadingCustomClass(col.key) : 'ao-p2-medium'} ${
								col.getHeadingCustomClass
									? col.getHeadingCustomClass(col.key)
									: ind !== 0
									? 'text-right'
									: 'text-left'
							}`,
							markHeader ? 'bg-lightGrey' : '',
						]"
						:style="hideHeading ? 'visibility: hidden; height: 0' : ''"
					>
						<slot
							:name="`heading-${col.key}`"
							:col="col"
						>
							<span class="py-3 px-1">
								{{ col.title }}
							</span>
						</slot>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr
					v-for="(item, index) in data"
					:key="index"
					class="dashboard-simple-table__row"
					:class="[
						item.rowSettings && item.rowSettings.borderColor
							? 'row-border-' + item.rowSettings.borderColor
							: '',
						(markEven ? markEven && (index + 1) % 2 === 0 : markOdd && (index + 1) % 2 !== 0)
							? 'bg-lightGrey'
							: '',
						getRowClass ? getRowClass(item, index) : '',
					]"
				>
					<td
						v-for="col in cols"
						:key="col.key"
						:class="
							col.getCellCustomClass
								? col.getCellCustomClass(col.key, item[col.key], index)
								: 'ao-p2-medium text-textSecondary800 '
						"
						class="dashboard-simple-table__row"
					>
						<slot
							:name="`cell-${col.key}`"
							:cellValue="item[col.key]"
							:index="index"
							:rowValue="item"
						>
							<span class="dashboard-simple-table__row-cell-item py-3 px-1">
								{{ item[col.key] }}
							</span>
						</slot>
					</td>
				</tr>
			</tbody>
			<template v-slot:bottom>
				<div class="d-flex align-items-start justify-space-between">
					<slot name="footer" />
				</div>
			</template>
		</v-table>
	</div>
</template>
<script>
import { computed, ref, onMounted } from 'vue'

import EmptyStateBlock from '@/components/ui/EmptyStateBlock.vue'

/* Размеры заглушки. `compact` — то, что таблица рисовала до появления пропа, поэтому он
   и остаётся умолчанием: существующие виджеты своим видом не платят за новый вариант.
   Иконка задаётся числом (проп `size` у `ao-icon` кладёт `font-size` в пиксели),
   подписи — классами типографики, а не своими размерами */
const EMPTY_VARIANTS = {
	compact: {
		iconSize: 18,
		iconIndentClass: 'mb-2',
		titleClass: 'ao-p2-medium',
		subtitleClass: 'ao-p4-regular',
	},
	medium: {
		iconSize: 32,
		iconIndentClass: 'mb-3',
		titleClass: 'ao-p1',
		subtitleClass: 'ao-p2-regular',
	},
	large: {
		iconSize: 40,
		iconIndentClass: 'mb-4',
		titleClass: 'ao-h2',
		subtitleClass: 'ao-p1-regular',
	},
}

Object.freeze(EMPTY_VARIANTS)

export default {
	name: 'DashboardSimpleTable',
	components: {
		EmptyStateBlock,
	},
	props: {
		density: {
			type: String,
			default: 'compact',
		},
		autoHeight: {
			type: Boolean,
			default: false,
		},
		cols: {
			type: Array,
			default: () => [
				/*
					{
						title: 'Name',
						key: 'name',
						getHeadingCustomClass: (headingKey) => '',
						getCellCustomClass: (headingKey, cellValue) => '',
					}
				*/
			],
		},
		data: {
			type: Array,
			default: () => [],
		},
		headerBorderColor: {
			type: String,
			default: '', //['', 'green', 'blue', 'orange']
		},
		titleHeader: {
			type: Array,
			default: () => [],
		},
		markEven: {
			type: Boolean,
			default: false,
		},
		markOdd: {
			type: Boolean,
			default: false,
		},
		hideHeading: {
			type: Boolean,
			default: false,
		},
		getRowClass: {
			type: Function,
			default: () => '',
		},
		markHeader: {
			type: Boolean,
			default: false,
		},
		isEmpty: {
			type: Boolean,
			default: false,
		},
		/* Размер встроенной заглушки: 'compact' | 'medium' | 'large'.
		   Своё содержимое заглушки по-прежнему задаётся слотом `empty` — проп влияет
		   только на встроенное */
		emptyVariant: {
			type: String,
			default: 'compact',
			validator: (value) => Object.keys(EMPTY_VARIANTS).includes(value),
		},
	},
	setup(props) {
		const resizeTrigger = ref(1)

		const tableWrapper = ref(null)
		const calculatedHeight = computed(() => {
			return resizeTrigger.value && tableWrapper.value ? tableWrapper.value.clientHeight : 0
		})

		// RISK(RISK-024)[S3/краш]: анонимный обработчик resize нельзя снять (нет removeEventListener/
		// onUnmounted) — каждая таблица навсегда оставляет слушатель с замыканием на мёртвый resizeTrigger;
		// на дашборде с десятками виджетов ресайз окна вызывает лавину апдейтов. Решение: useEventListener
		// (@vueuse) или именованный обработчик + onUnmounted. docs/audit/04_risks_register.md (RISK-024)
		onMounted(() => {
			window.addEventListener('resize', () => {
				resizeTrigger.value += 1
			})
		})

		const emptySizes = computed(() => EMPTY_VARIANTS[props.emptyVariant])

		return {
			tableWrapper,
			calculatedHeight,
			emptySizes,
		}
	},
}
</script>
<style lang="scss">
.dashboard-simple-table {
	width: 100%;
	height: 100%;
	max-height: 100%;
	overflow-x: hidden;
	overflow-y: auto;

	& .v-table {
		height: 100%;
	}

	& .v-table .v-table__wrapper {
		height: 100% !important;
	}

	& .v-table > .v-row > .v-col {
		font-weight: 500;
		font-size: 14px;
		line-height: 145%;
		text-align: right;
		color: #3f464e;
		&:not(:first-child) {
			color: #1d2126;
		}
	}

	& .v-table .v-table__wrapper > table > thead > tr > th {
		font-style: normal;
		color: #1d2126;
		// padding-left: 0;
		// padding-right: 0;
		padding-left: 4px;
		padding-right: 4px;
	}
	.v-table .v-table__wrapper .row-border {
		&-grey td {
			border-bottom: 1px solid #dee6e8 !important;
		}
	}
	// .v-table .v-table__wrapper .row-border-grey:last-child td {
	// 	border-bottom: none !important;
	// }

	// СТИЛИ ЯЧЕЕК ВНУТРИ ТАБЛИЦЫ
	& .v-table .v-table__wrapper {
		font-size: 12px;
		font-weight: 500;
		line-height: 150%;
		& .sm-text {
			font-size: 10px;
		}
		& table > tbody {
			& tr {
				color: #576674; // первый столбец title
				& td:not(:first-child) {
					color: #3f464e; // остальные столбцы
					text-align: right;
				}
				& td {
					padding-left: 4px;
					padding-right: 4px;
				}
			}
			& tr:not(:last-child) > td {
				border-color: transparent; // все кроме последнего строки
			}
		}
	}
	// ЦВЕТА ГОРИЗОНТАЛЬНЫХ ПОЛОСОК
	&--color-green {
		& .v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th {
			box-shadow: inset 0 -2px 0 #cce19f;
		}
	}

	&--color-blue {
		& .v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th {
			box-shadow: inset 0 -2px 0 #3f73ab;
		}
	}

	&--color-lightBlue {
		& .v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th {
			box-shadow: inset 0 -2px 0 #89b4dc;
		}
	}

	&--color-orange {
		& .v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th {
			box-shadow: inset 0 -2px 0 #f7bead;
		}
	}

	&--color-amber {
		& .v-table.v-table--fixed-header > .v-table__wrapper > table > thead > tr > th {
			box-shadow: inset 0 -2px 0 #eab631;
		}
	}
}
</style>
